/**
 * An airline, as the dictionary stores it. The API calls this shape `Aircompany`.
 *
 * Every schedule integration resolves a flight's carrier against this dictionary by
 * IATA code, so a write here is an edit to shared master data rather than a local
 * one.
 *
 * **An edit is a REPLACE, not a patch.** The server writes every field it is sent and
 * writes `null`/`false` for every one it is not, so a body built from a form alone
 * clears whatever the form does not carry. `AirlinePanel` builds each edit body by
 * spreading the row it loaded.
 *
 * **There are no `created` / `updated` fields.** Unlike the airport dictionary this
 * table keeps no audit timestamps, so an edit cannot be dated and no screen should
 * offer a "last modified" column.
 */
export interface Airline {
  id: number;
  /**
   * The two-character IATA designator — the natural unique key, and what the
   * schedule filters and sorts on. Nullable on read; required, and exactly two
   * characters, on write.
   */
  iataCode: string | null;
  /**
   * The three-character ICAO designator, unique among the airlines that have one.
   *
   * **Trim before using it.** Rows seeded before this API existed put `''` into a
   * `bpchar(3)` column, so they come back as three spaces rather than as null. Saving
   * such a row through `edit` normalizes it away.
   */
  icaoCode: string | null;
  /** The airway-bill prefix, up to three characters — `465` for Air Astana. */
  prefix?: string | null;
  /** The short name, which is what a list shows. Required on write. */
  title: string | null;
  /** The legal name. */
  fullTitle?: string | null;
  description?: string | null;
  /**
   * Whether the carrier is on the working list.
   *
   * **The name reads oddly against its meaning.** The JSON key is `kzFlying`, but the
   * column behind it is `signed` — "контракт подписан" — and it is what
   * `GET /airline/all` filters on. So it means "included in the working carrier
   * list", not "flies to Kazakhstan", and the screen labels it for the meaning rather
   * than for the key.
   */
  kzFlying?: boolean;
}

/**
 * The lengths the server enforces, mirrored so a bad code costs no round trip.
 *
 * These are not style rules: the columns are fixed-length `bpchar`, so a longer value
 * is a database error and a shorter one is silently space-padded. That padding is
 * exactly how the legacy `icaoCode` rows above came to exist.
 */
export const AIRLINE_LENGTHS = {
  iataCode: 2,
  icaoCode: 3,
  /** A maximum rather than an exact length. */
  prefix: 3,
  title: 256,
  fullTitle: 256,
} as const;

/**
 * The ICAO code with the legacy padding removed, or `null` when there is none.
 *
 * `'   '` and `''` both mean "this carrier has no ICAO code", and only one of them
 * looks like it.
 */
export const icaoOf = (airline: Airline): string | null => {
  const code = airline.icaoCode?.trim() ?? '';
  return code === '' ? null : code;
};

/** Whether the carrier is on the working list — see `kzFlying` above. */
export const isWorkingCarrier = (airline: Airline): boolean => airline.kzFlying ?? false;
